import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Topbar } from "@/components/layout/Topbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/rikkus/StatusBadge";
import { TimestampET } from "@/components/rikkus/TimestampET";

const emptyDraft = (r) => ({
    program_number: r.program_number,
    horse_name: r.horse_name,
    scratched: r.scratched,
    was_existing_nonnull: r.finish_position != null,
    existing_source: r.source,
    existing_entered_by: r.entered_by,
    existing_revised_by: r.revised_by,
    // editable fields
    finish_position: r.finish_position ?? "",
    dead_heat: !!r.dead_heat,
    dq_flag: !!r.dq_flag,
    dq_original_position: r.dq_original_position ?? "",
    dnf_flag: !!r.dnf_flag,
    dnf_reason: r.dnf_reason ?? "",
});

const draftEqualsExisting = (d, r) => {
    const eqPos = (a, b) => {
        const av = a === "" || a == null ? null : Number(a);
        const bv = b == null ? null : Number(b);
        return av === bv;
    };
    return (
        eqPos(d.finish_position, r.finish_position)
        && !!d.dead_heat === !!r.dead_heat
        && !!d.dq_flag === !!r.dq_flag
        && eqPos(d.dq_original_position, r.dq_original_position)
        && !!d.dnf_flag === !!r.dnf_flag
        && (d.dnf_reason || "") === (r.dnf_reason || "")
    );
};

export default function ManualResultsEditorPage() {
    const { raceId } = useParams();
    const nav = useNavigate();
    const [state, setState] = useState(null);
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [revisionNote, setRevisionNote] = useState("");

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/races/${raceId}/manual-editor`)
            .then((r) => {
                setState(r.data);
                setDrafts((r.data.rows || []).map(emptyDraft));
            })
            .catch((e) => toast.error(e?.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    }, [raceId]);

    useEffect(() => { load(); }, [load]);

    // Filas cambiadas (excluye scratched — no se pueden marcar posición)
    const changedRows = useMemo(() => {
        if (!state) return [];
        return drafts
            .map((d, i) => ({ d, orig: state.rows[i] }))
            .filter(({ d, orig }) => !d.scratched && !draftEqualsExisting(d, orig));
    }, [drafts, state]);

    // Filas que están reescribiendo un valor NON-NULL previo
    const overwriteRows = useMemo(
        () => changedRows.filter(({ d }) => d.was_existing_nonnull),
        [changedRows]
    );

    // Validaciones locales antes de POST
    const validate = () => {
        const seenPos = new Map();
        for (const { d } of changedRows) {
            if (d.dq_flag && d.dnf_flag) {
                return `#${d.program_number}: dq y dnf no pueden marcarse juntos`;
            }
            if (d.dq_flag && !d.dq_original_position) {
                return `#${d.program_number}: dq requiere dq_original_position`;
            }
            if (d.dnf_flag) {
                if (d.finish_position !== "" && d.finish_position != null) {
                    return `#${d.program_number}: dnf requiere finish_position vacío`;
                }
                if (!d.dnf_reason.trim()) {
                    return `#${d.program_number}: dnf requiere motivo`;
                }
            }
            if (!d.dq_flag && d.dq_original_position) {
                return `#${d.program_number}: dq_original_position solo aplica con dq_flag`;
            }
            // uniqueness of finish_position (except dead_heat)
            if (d.finish_position !== "" && d.finish_position != null && !d.dead_heat) {
                const key = Number(d.finish_position);
                if (seenPos.has(key)) {
                    return `finish_position ${key} duplicado sin dead_heat entre #${seenPos.get(key)} y #${d.program_number}`;
                }
                seenPos.set(key, d.program_number);
            }
        }
        return null;
    };

    const buildBody = (revNote) => {
        const rows = changedRows.map(({ d }) => ({
            program_number: d.program_number,
            finish_position: d.finish_position === "" ? null : Number(d.finish_position),
            dead_heat: !!d.dead_heat,
            dq_flag: !!d.dq_flag,
            dq_original_position: d.dq_original_position === "" ? null : Number(d.dq_original_position),
            dnf_flag: !!d.dnf_flag,
            dnf_reason: d.dnf_reason?.trim() || null,
        }));
        return { rows, revision_note: revNote || null };
    };

    const doSubmit = async (revNote) => {
        setSaving(true);
        try {
            const { data } = await api.post(`/races/${raceId}/manual-results`, buildBody(revNote));
            const label = [];
            if (data.inserted) label.push(`${data.inserted} nuevas`);
            if (data.updated) label.push(`${data.updated} completadas`);
            if (data.revised) label.push(`${data.revised} revisadas`);
            if (data.skipped) label.push(`${data.skipped} sin cambio`);
            toast.success(`Guardado — ${label.join(", ") || "sin cambios"}`);
            setConfirmOpen(false);
            setRevisionNote("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    const onSaveClick = () => {
        if (changedRows.length === 0) {
            toast.info("No hay cambios que guardar");
            return;
        }
        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }
        if (overwriteRows.length > 0) {
            // Requiere revision_note obligatorio
            setConfirmOpen(true);
            return;
        }
        doSubmit(null);
    };

    const updateDraft = (idx, patch) => {
        setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
    };

    return (
        <>
            <Topbar
                title={`Editor manual — R${state?.race_number ?? "?"} ${state?.track_id ?? ""}`}
                subtitle={state ? `${state.race_date} · post ${new Date(state.scheduled_post_at).toISOString().slice(11, 16)}Z · TOP_K máx ${state.top_k_max ?? "—"}` : ""}
            />
            <div className="p-3 md:p-4 space-y-4 flex-1 overflow-auto">
                <div className="flex items-center justify-between">
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => nav(-1)}>
                        <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Volver
                    </Button>
                    <div className="flex items-center gap-2">
                        <span
                            data-testid="manual-editor-changes-count"
                            className="text-[11px] font-mono text-muted-foreground"
                        >
                            {changedRows.length} cambio(s) · {overwriteRows.length} overwrite(s)
                        </span>
                        <Button
                            data-testid="manual-editor-save-btn"
                            size="sm"
                            className="h-8 text-xs"
                            disabled={saving || changedRows.length === 0}
                            onClick={onSaveClick}
                        >
                            <Save className="h-3.5 w-3.5 mr-2" />
                            {saving ? "Guardando…" : "Guardar cambios"}
                        </Button>
                    </div>
                </div>

                {loading || !state ? <Skeleton className="h-64 w-full" /> : (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm">Orden de llegada</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="rounded-md border border-border overflow-x-auto">
                                <Table data-testid="manual-editor-table">
                                    <TableHeader>
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            <TableHead className="h-8 text-[11px] w-10">#</TableHead>
                                            <TableHead className="h-8 text-[11px]">Caballo</TableHead>
                                            <TableHead className="h-8 text-[11px] w-24">Pos</TableHead>
                                            <TableHead className="h-8 text-[11px] w-16">DH</TableHead>
                                            <TableHead className="h-8 text-[11px] w-16">DQ</TableHead>
                                            <TableHead className="h-8 text-[11px] w-20">DQ orig.</TableHead>
                                            <TableHead className="h-8 text-[11px] w-16">DNF</TableHead>
                                            <TableHead className="h-8 text-[11px]">DNF motivo</TableHead>
                                            <TableHead className="h-8 text-[11px]">Origen</TableHead>
                                            <TableHead className="h-8 text-[11px]">Auditoría</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {drafts.map((d, i) => {
                                            const orig = state.rows[i];
                                            const changed = !d.scratched && !draftEqualsExisting(d, orig);
                                            const isOverwrite = changed && d.was_existing_nonnull;
                                            const rowCls = d.scratched
                                                ? "bg-muted/20 text-muted-foreground"
                                                : isOverwrite
                                                    ? "bg-warn-bg"
                                                    : changed
                                                        ? "bg-primary/5"
                                                        : "";
                                            return (
                                                <TableRow
                                                    key={d.program_number}
                                                    data-testid={`manual-editor-row-${d.program_number}`}
                                                    className={`h-9 ${rowCls}`}
                                                >
                                                    <TableCell className="font-mono text-xs">{d.program_number}</TableCell>
                                                    <TableCell className="text-xs">
                                                        {d.horse_name}
                                                        {d.scratched && <StatusBadge kind="miss" className="ml-2">SCR</StatusBadge>}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            data-testid={`manual-editor-pos-input-${d.program_number}`}
                                                            type="number"
                                                            min={1}
                                                            max={30}
                                                            disabled={d.scratched || d.dnf_flag}
                                                            value={d.finish_position}
                                                            onChange={(e) => updateDraft(i, { finish_position: e.target.value })}
                                                            className="h-7 w-20 font-mono text-xs"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Checkbox
                                                            data-testid={`manual-editor-dh-${d.program_number}`}
                                                            disabled={d.scratched}
                                                            checked={d.dead_heat}
                                                            onCheckedChange={(v) => updateDraft(i, { dead_heat: !!v })}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Checkbox
                                                            data-testid={`manual-editor-dq-${d.program_number}`}
                                                            disabled={d.scratched || d.dnf_flag}
                                                            checked={d.dq_flag}
                                                            onCheckedChange={(v) => updateDraft(i, { dq_flag: !!v })}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            data-testid={`manual-editor-dq-orig-${d.program_number}`}
                                                            type="number"
                                                            min={1}
                                                            max={30}
                                                            disabled={!d.dq_flag}
                                                            value={d.dq_original_position}
                                                            onChange={(e) => updateDraft(i, { dq_original_position: e.target.value })}
                                                            className="h-7 w-16 font-mono text-xs"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Checkbox
                                                            data-testid={`manual-editor-dnf-${d.program_number}`}
                                                            disabled={d.scratched || d.dq_flag}
                                                            checked={d.dnf_flag}
                                                            onCheckedChange={(v) => updateDraft(i, {
                                                                dnf_flag: !!v,
                                                                finish_position: v ? "" : d.finish_position,
                                                            })}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            data-testid={`manual-editor-dnf-reason-${d.program_number}`}
                                                            disabled={!d.dnf_flag}
                                                            placeholder="pulled up, unseated rider, …"
                                                            value={d.dnf_reason}
                                                            onChange={(e) => updateDraft(i, { dnf_reason: e.target.value })}
                                                            className="h-7 text-xs"
                                                        />
                                                    </TableCell>
                                                    <TableCell className="text-[10px] font-mono">
                                                        {orig.source
                                                            ? <StatusBadge kind={orig.source === "MANUAL_ENTRY" ? "verified" : "partial"}>{orig.source}</StatusBadge>
                                                            : <span className="text-muted-foreground">—</span>}
                                                    </TableCell>
                                                    <TableCell className="text-[10px] font-mono text-muted-foreground">
                                                        {orig.entered_by && <div>ent: {orig.entered_by.split("@")[0]} <TimestampET value={orig.entered_at} /></div>}
                                                        {orig.revised_by && <div className="text-warn">rev: {orig.revised_by.split("@")[0]} <TimestampET value={orig.revised_at} /></div>}
                                                        {!orig.entered_by && !orig.revised_by && <span>—</span>}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Modal para overwrite */}
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent data-testid="manual-editor-overwrite-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-warn" />
                            Sobreescribir resultados existentes
                        </DialogTitle>
                        <DialogDescription>
                            Estás por sobreescribir {overwriteRows.length} fila(s) con <code>finish_position</code> ya ingresado.
                            La revisión queda registrada (<code>revised_by</code>, <code>revised_at</code>, <code>revision_note</code>).
                            El motivo es obligatorio.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-md border border-warn/40 bg-warn-bg p-3">
                            <div className="text-[11px] font-mono text-warn uppercase tracking-wide mb-1">
                                Filas afectadas
                            </div>
                            <ul className="text-xs space-y-0.5 font-mono">
                                {overwriteRows.map(({ d, orig }) => (
                                    <li key={d.program_number}>
                                        #{d.program_number} {d.horse_name}: <strong>{orig.finish_position}</strong> → <strong>{d.finish_position || (d.dnf_flag ? "DNF" : "?")}</strong>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <label className="text-xs mb-1 block">Motivo de la revisión (obligatorio)</label>
                            <Textarea
                                data-testid="manual-editor-revision-note-input"
                                rows={3}
                                value={revisionNote}
                                onChange={(e) => setRevisionNote(e.target.value)}
                                placeholder="Ej: chart oficial post-race corrige posición 5 → 6"
                                className="font-mono text-xs"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => setConfirmOpen(false)}
                            disabled={saving}
                        >
                            Cancelar
                        </Button>
                        <Button
                            data-testid="manual-editor-confirm-overwrite-btn"
                            size="sm"
                            className="h-8 text-xs"
                            disabled={saving || !revisionNote.trim()}
                            onClick={() => doSubmit(revisionNote.trim())}
                        >
                            {saving ? "Guardando…" : "Confirmar y guardar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
